// How Checkout turns its authoritative state into POST /orders, and how it
// reads the backend's answer. Nothing here prices anything: every amount sent
// is the checkout quote's own, and every decision after sending is the
// backend's (customerAppOrdersController.placeOrder).

/**
 * The order request, exactly as the backend reads it:
 *
 *   items               what the customer asked for (productId, quantity)
 *   deliveryAddress     the address Checkout quoted for (pincode + coordinates)
 *   paymentMethod       'cod' | 'online'
 *   deliverySlotId      a slot from THIS address's slot list
 *   couponCode          the customer's submitted code, whenever there is one —
 *                       even if the checkout quote refused it: placement
 *                       re-evaluates it at the outlet that actually takes the
 *                       order, and any change comes back as QUOTE_CHANGED
 *   quoteToken          the checkout quote's token, only when it issued one
 *                       (a non-binding quote has none; none is invented)
 *   expectedGrandTotal  the checkout quote's grandTotal, always — the total
 *                       the customer reviewed, compared (never trusted)
 */
export const buildOrderPayload = ({
  items,
  deliveryAddress,
  paymentMethod,
  deliverySlotId,
  couponCode = null,
  quoteToken = null,
  expectedGrandTotal,
}) => ({
  items: items.map((item) => ({ productId: item.productId, quantity: item.quantity })),
  deliveryAddress,
  paymentMethod,
  deliverySlotId,
  fulfillmentMode: 'allow_split',
  ...(couponCode ? { couponCode } : {}),
  ...(quoteToken ? { quoteToken } : {}),
  expectedGrandTotal,
});

/**
 * One intended order attempt. The backend binds an Idempotency-Key to the
 * exact request body (a different body under the same key is refused), so the
 * key must follow the payload: the same attempt keeps its key, any change to
 * what would be sent — address, slot, payment, coupon, cart, quote, total —
 * is a new attempt with a new key.
 */
export const attemptFingerprint = (quoteKey, payload) => JSON.stringify({ quoteKey, payload });

/**
 * Whether the outcome of a failed placement is unknown — the request may have
 * reached the backend and still be (or have been) processed. Only then is the
 * same key reused for a retry, so the backend replays or reports the original
 * instead of creating a second order.
 */
// Placement answers that PROVE the backend refused the order and created
// nothing: its own validation or business refusal (400, 404, 409, 410, 412,
// 422), except the codes that only describe the key's state. Never proof:
// authentication or permission (401, 403 — the request was not judged, or
// the session changed), "not now" (408, 425, 429), 5xx, no answer, or any
// other status. Those leave the original order possibly placed: uncertain.
const REFUSAL_STATUSES = new Set([400, 404, 409, 410, 412, 422]);
const NOT_A_REFUSAL_CODES = new Set([
  'IDEMPOTENCY_REQUEST_IN_PROGRESS',
  'IDEMPOTENCY_KEY_REUSED_WITH_DIFFERENT_BODY',
  'ORDER_ALREADY_PLACED',
  'SESSION_CHANGED',
  'SESSION_EXPIRED',
  'NETWORK_ERROR',
]);
export const isDefinitePlacementRefusal = (error) => {
  const status = Number(error?.status);
  return REFUSAL_STATUSES.has(status) && !NOT_A_REFUSAL_CODES.has(error?.code);
};
export const isUncertainOutcome = (error) => !isDefinitePlacementRefusal(error);

// How long to wait before asking again after "not now" (429 etc.).
export const retryDelayMs = (error) => (Number.isFinite(error?.retryAfterSeconds) ? error.retryAfterSeconds * 1000 : 2000);

// A replay must reach the backend while its idempotency record still lives;
// this much of the window is never used, for clock and network slack.
export const REPLAY_SAFETY_MARGIN_MS = 10 * 60 * 1000;

/**
 * What to do with an unresolved placement attempt, from the backend's own
 * answer about its key (GET /orders/attempts/:key):
 *   replay   POST the same key + exact body: the backend still replays it
 *            (live claim), or provably nothing was ever made for it and it
 *            is still inside the replay window
 *   sale     an order exists and can no longer be replayed: act on the order
 *   retire   provably no order, and the window is over: nothing to recover
 *   wait     the backend is still processing that key
 *   unknown  cannot be proven either way — no POST, no new order
 * Times are compared on the SERVER's clock (serverTime), with the attempt's
 * local timestamp moved onto it.
 */
export const recoveryDecision = (attempt, info, clientNow = Date.now()) => {
  if (!info || typeof info !== 'object') return { action: 'unknown' };
  const serverNow = Date.parse(info.serverTime);
  if (!Number.isFinite(serverNow)) return { action: 'unknown' };
  const claim = info.claim;
  if (info.sale?.finalized || info.sale?.checkoutGroup) {
    return { action: 'sale', sale: info.sale };
  }
  if (claim?.state === 'in_progress') return { action: 'wait' };
  const replayProtected = claim?.state === 'completed' && Date.parse(claim.expiresAt) - serverNow > REPLAY_SAFETY_MARGIN_MS;
  if (info.sale) return replayProtected ? { action: 'replay' } : { action: 'sale', sale: info.sale };
  if (replayProtected) return { action: 'replay' };
  if (claim) return { action: 'unknown' };
  // No order and no claim. That proves nothing was made only when the backend
  // has verified, in its own data, that no order which could be this
  // attempt's was created without recording its key (coverage).
  if (info.coverage?.verified !== true) return { action: 'unknown' };
  const createdOnServer = Number(attempt?.createdAt) + (serverNow - clientNow);
  if (!Number.isFinite(createdOnServer)) return { action: 'unknown' };
  const ttlMs = Number(info.ttlSeconds) * 1000;
  if (Number.isFinite(ttlMs) && serverNow - createdOnServer < ttlMs - REPLAY_SAFETY_MARGIN_MS) return { action: 'replay' };
  return { action: 'retire' };
};

/** Slot refusals, before or after allocation (utils/deliverySlots.js codes). */
export const SLOT_CONFLICT_CODES = new Set([
  'DELIVERY_SLOT_REQUIRED',
  'INVALID_DELIVERY_SLOT',
  'DELIVERY_SLOT_EXPIRED',
  'NO_DELIVERY_SLOTS_AVAILABLE',
  'DELIVERY_SLOTS_UNAVAILABLE',
]);

// QUOTE_CHANGED reasons (QUOTE_CHANGED_MESSAGES), in the customer's words. No
// outlet is named; an unknown reason falls back to the backend's message.
const QUOTE_CHANGE_REASONS = {
  price_changed: 'Some prices changed since you reviewed your order.',
  outlet_changed: 'Your order will come from a different store, where prices differ.',
  offer_changed: 'An offer on your order changed.',
  offer_expired: 'An offer on your order has expired.',
  offer_exhausted: 'An offer on your order has just run out.',
  quote_expired: 'Your price had expired, so we refreshed it.',
  quote_invalid: 'We had to refresh your price.',
  channel_changed: 'Your order was priced again for our website.',
};

export const quoteChangeReason = (reason, backendMessage) =>
  QUOTE_CHANGE_REASONS[reason] || backendMessage || 'Your order total changed.';

// COD_NOT_AVAILABLE reasons (checkCodEligibility).
const COD_REASONS = {
  disabled_globally: 'Cash on Delivery is currently unavailable.',
  over_max_value: 'Cash on Delivery isn’t available for an order of this value.',
  disabled_for_pincode: 'Cash on Delivery isn’t available at this address.',
  disabled_for_customer: 'Cash on Delivery isn’t available for your account.',
};

export const codUnavailableMessage = (reason) => COD_REASONS[reason] || 'Cash on Delivery isn’t available for this order.';

/**
 * Cash on Delivery as far as the customer-facing payment settings say — the
 * settings the backend publishes for exactly this purpose — for this address
 * and this checkout quote's total. The backend re-checks at placement (and
 * knows per-customer restrictions this cannot); its refusal is handled too.
 * Returns null when COD is available, else a reason key.
 */
export const codRestriction = (paymentSettings, { pincode, grandTotal }) => {
  if (!paymentSettings) return null;
  if (paymentSettings.allowCashOnDelivery === false) return 'disabled_globally';
  const disabled = Array.isArray(paymentSettings.codDisabledPincodes) ? paymentSettings.codDisabledPincodes : [];
  if (pincode && disabled.map((p) => String(p).trim()).includes(String(pincode).trim())) return 'disabled_for_pincode';
  const max = Number(paymentSettings.codMaxOrderValue);
  if (max > 0 && typeof grandTotal === 'number' && grandTotal > max) return 'over_max_value';
  return null;
};

/**
 * A verified online payment, as POST /orders/:id/confirm-payment reports it:
 * the backend marks it `paid`, or the sale is already invoiced/completed.
 * Anything else (refund pending, refunded, not captured) is not a success.
 */
// The ORDER is done — not merely its payment captured.
export const isConfirmedPayment = (data) => ['invoiced', 'completed'].includes(data?.saleStatus);

// Where a remembered (queued) order stands, from the backend's own answer to
// GET /orders/:id/payment-status — never from time passing on this page.
//   complete        invoiced / completed: the order is placed
//   finalizing      paid, but the order is still a draft: being finished
//   pending         still waiting for a store
//   ready           a store took it; the customer continues it
//   expired         the backend cancelled it because the time to pay ran out
//   failed          cancelled or rejected for another reason
//   refund_pending  money was taken and its refund is still being processed
//   refunded        money was taken and has been refunded
// null when the answer says nothing the page can act on.
export const pendingOrderOutcome = (data) => {
  const status = data?.status;
  const paymentStatus = data?.paymentStatus;
  if (['invoiced', 'completed'].includes(status)) return 'complete';
  if (paymentStatus === 'refund_pending') return 'refund_pending';
  if (paymentStatus === 'refunded' || status === 'refunded') return 'refunded';
  if (status === 'pending_allocation') return 'pending';
  // Paid, but the order is not finished yet (finalization interrupted): the
  // backend finishes it; it is neither done nor payable again.
  if (status === 'draft') return paymentStatus === 'completed' ? 'finalizing' : 'ready';
  if (['cancelled', 'rejected'].includes(status)) return data.paymentTimedOut ? 'expired' : 'failed';
  return null;
};

// What the customer is told about a refund. Only `refunded` says the money is
// back; `refund_pending` means the refund has started and is not complete.
export const REFUND_MESSAGES = {
  refund_pending: "This order wasn't placed. Your refund is being processed, and we'll update the order when it's complete.",
  refunded: "This order wasn't placed. Your payment has been refunded.",
};
